import React, { useState, useEffect } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    LinearProgress,
    Chip,
    Alert,
    AlertTitle
} from '@mui/material';
import {
    DirectionsCar,
    Speed,
    TrendingUp
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { fuelApi, FuelTransaction } from '../services/fuelApi';
import { mileageApi, MileageLog } from '../services/mileageApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

const GeneralAnalysis: React.FC = () => {
    const { t } = useLanguage();
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [transactions, setTransactions] = useState<FuelTransaction[]>([]);
    const [mileage, setMileage] = useState<MileageLog[]>([]);

    const loadData = async () => {
        try {
            const [vehRes, txRes, kmRes] = await Promise.all([
                vehiclesApi.getAll(),
                fuelApi.getAll(),
                mileageApi.getAll()
            ]);
            
            if (vehRes.success) setVehicles(vehRes.data);
            if (txRes.success) setTransactions(txRes.data);
            if (kmRes.success) setMileage(kmRes.data);
        } catch (error) {
            console.error('Error loading analytics data:', error);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Statistics calculations
    const totalVehicles = vehicles.length;
    const totalKms = mileage.reduce((sum, log) => sum + (log.distance || 0), 0);
    const totalFuelCost = transactions.reduce((sum, tx) => sum + (tx.cost || 0), 0);
    const totalLiters = transactions.reduce((sum, tx) => sum + (tx.liters || 0), 0);
    
    // Average consumption (L/100km)
    const avgConsumption = totalKms > 0 ? (totalLiters / totalKms) * 100 : 0;
    
    // Health Rating based on active status or recent repairs
    const vehicleInService = vehicles.filter(v => v.status === 'active').length;
    const fleetHealth = totalVehicles > 0 ? (vehicleInService / totalVehicles) * 100 : 100;

    // Predictions (e.g. average daily mileage and next oil change estimate)
    const warnings: string[] = [];
    if (fleetHealth < 80) {
        warnings.push(t('parc.analysis.warningMaintOrOut', (100 - fleetHealth).toFixed(0)));
    }

    return (
        <GenericPage title={t('parc.analysis.generalTitle') || "Analyse Générale de la Flotte"} footerEnabled={false}>
            <Grid container spacing={3}>
                {/* Warnings Banner */}
                {warnings.length > 0 && (
                    <Grid item xs={12}>
                        <Alert severity="warning" variant="filled">
                            <AlertTitle>{t('parc.analysis.warningTitle')}</AlertTitle>
                            {warnings.map((w, i) => <Typography key={i} variant="body2">{w}</Typography>)}
                        </Alert>
                    </Grid>
                )}

                {/* KPI Cards */}
                <Grid item xs={12} sm={4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: '#fff' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">{t('parc.dashboard.vehicles')}</Typography>
                                <DirectionsCar fontSize="large" />
                            </Box>
                            <Typography variant="h3">{totalVehicles}</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                {t('parc.analysis.vehiclesActiveImmobilized', vehicleInService, totalVehicles - vehicleInService)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">{t('parc.analysis.kmsTraveled')}</Typography>
                                <Speed fontSize="large" />
                            </Box>
                            <Typography variant="h3">{totalKms.toLocaleString()} km</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                {t('parc.analysis.kmsCumulatedDesc')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card sx={{ background: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)', color: '#fff' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">{t('parc.analysis.fuelExpenses')}</Typography>
                                <TrendingUp fontSize="large" />
                            </Box>
                            <Typography variant="h3">{totalFuelCost.toLocaleString('fr-FR')} DZD</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                {t('parc.analysis.litersConsumedTotal', totalLiters.toLocaleString())}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Health Rating progress */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ minHeight: 250 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('parc.analysis.availabilityIndex')}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={fleetHealth} 
                                        color={fleetHealth > 85 ? 'success' : fleetHealth > 60 ? 'warning' : 'error'}
                                        sx={{ height: 15, borderRadius: 5 }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 45 }}>
                                    <Typography variant="h5" color="text.secondary">{`${Math.round(fleetHealth)}%`}</Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                {t('parc.analysis.availabilityIndexDesc')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Predictions & Optimization */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ minHeight: 250 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('parc.analysis.optimizations')}</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1">{t('parc.analysis.avgConsumption')}</Typography>
                                    <Chip label={avgConsumption > 0 ? `${avgConsumption.toFixed(2)} L / 100 km` : t('parc.analysis.insufficientData')} color="primary" />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1">{t('parc.analysis.estAnnualFuel')}</Typography>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {(totalFuelCost * 1.2).toLocaleString('fr-FR')} DZD
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body1">{t('parc.analysis.nextOilChange')}</Typography>
                                    <Chip label={t('parc.analysis.nextOilChangeValue')} color="info" />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </GenericPage>
    );
};

export default GeneralAnalysis;
